import { RegisterValidationFieldValidation, RegisterValidationFieldValidationPattern } from '@api/dto';
import { TextFieldControl, TextFieldControlProps } from '@components/TextFieldControl';
import { FIELD_LABEL_EMPLOYEE_NUMBER_LH } from '@constants';
import Box from '@mui/material/Box';
import { TFunction } from 'i18next';
import { JSX } from 'react';
import { FieldPath, FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export type ValidatedTextFieldControlProps<T extends FieldValues, TName extends FieldPath<T>> = TextFieldControlProps<T, TName> & {
  registerValidation?: RegisterValidationFieldValidation;
};

const validatePatterns = (t: TFunction, patterns?: RegisterValidationFieldValidationPattern[], required?: boolean) => {
  return (value: string | null | undefined) => {
    if (!patterns) return true;
    if (!value && !required) {
      return true;
    }
    for (const { pattern, translation } of patterns) {
      try {
        const regex = new RegExp(pattern);
        if (!regex.test(value || '')) {
          return t(`employerSpecialConfig.${translation}`, { defaultValue: translation || t('validation.invalidFormat') });
        }
      } catch {
        // Invalid regex pattern from API, skip this validation
        continue;
      }
    }
    return true;
  };
};

export const ValidatedTextFieldControl = <T extends FieldValues, TName extends FieldPath<T>>({
  registerValidation,
  ...props
}: ValidatedTextFieldControlProps<T, TName>): JSX.Element => {
  const { t } = useTranslation();

  // Lufthansa special case: if the field label is exactly <FIELD_LABEL_EMPLOYEE_NUMBER_LH>,
  // it should be displayed as a hint below the field instead of as the field label

  return (
    <TextFieldControl
      {...props}
      label={
        registerValidation?.fieldLabel && registerValidation.fieldLabel !== FIELD_LABEL_EMPLOYEE_NUMBER_LH
          ? t(`employerSpecialConfig.${registerValidation.fieldLabel}`, {
              defaultValue: registerValidation.fieldLabel.replace(/\*$/, '').trim(),
            })
          : props.label
      }
      helperText={
        <>
          {registerValidation?.fieldLabel === FIELD_LABEL_EMPLOYEE_NUMBER_LH && (
            <Box component="span" sx={{ mb: 1, display: 'block' }}>
              {t(`employerSpecialConfig.${FIELD_LABEL_EMPLOYEE_NUMBER_LH}`)}
            </Box>
          )}
          {props.helperText}
        </>
      }
      rules={{
        ...props.rules,
        required: registerValidation?.required ?? props.rules?.required,
        minLength: registerValidation?.minLen ?? props.rules?.minLength,
        maxLength: registerValidation?.maxLen ?? props.rules?.maxLength,
        validate: validatePatterns(t, registerValidation?.patterns, registerValidation?.required),
      }}
    />
  );
};
